import React, { useState, useEffect } from 'react';
import Topbar from '../components/layout/Topbar';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { usersApi, refApi, formatApiError } from '../lib/api';
import { ROLES } from '../lib/countryConfig';

function genTempPassword() {
  // Generate a memorable temporary password: GDxxxx-yyyy
  const r = Math.random().toString(36).slice(2, 6).toUpperCase();
  const r2 = Math.floor(Math.random() * 9000 + 1000);
  return `GD-${r}-${r2}`;
}

export default function Admin() {
  const { profile } = useAuth();
  const { country, countryCode } = useCountry();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('users');
  const [communes, setCommunes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Create user form
  const [form, setForm] = useState({
    email: '', full_name: '', role: 'agent',
    commune_id: '', region_id: '', country_mode: countryCode,
    temp_password: genTempPassword(),
  });
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, cRes, rRes] = await Promise.all([
        usersApi.list(),
        refApi.communes(),
        refApi.regions(),
      ]);
      setUsers(uRes.data || []);
      setCommunes(cRes.data || []);
      setRegions(rRes.data || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e?.preventDefault();
    setError('');
    setMsg('');
    setCreating(true);
    try {
      const { data } = await usersApi.create({
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        role: form.role,
        commune_id: form.commune_id || null,
        region_id: form.region_id || null,
        temp_password: form.temp_password,
      });
      setLastCreated({ ...data, temp_password: form.temp_password });
      setMsg(`✅ Compte créé. Communiquez ce mot de passe temporaire à l'utilisateur — il devra le changer à la 1ʳᵉ connexion.`);
      setForm({
        email: '', full_name: '', role: 'agent',
        commune_id: '', region_id: '', country_mode: countryCode,
        temp_password: genTempPassword(),
      });
      load();
    } catch (ex) {
      setError(formatApiError(ex));
    } finally {
      setCreating(false);
    }
  };

  const toggleActif = async (u) => {
    try {
      await usersApi.update(u.id, { actif: !u.actif });
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const resetPassword = async (u) => {
    const newPwd = genTempPassword();
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.full_name || u.email} ?\nNouveau mot de passe temporaire : ${newPwd}`)) return;
    try {
      await usersApi.resetPassword(u.id, newPwd);
      setLastCreated({ ...u, temp_password: newPwd, _reset: true });
      setMsg(`🔁 Mot de passe réinitialisé. Communiquez ${newPwd} à ${u.email} — il devra le changer à la prochaine connexion.`);
    } catch (e) { setError(formatApiError(e)); }
  };

  const removeUser = async (u) => {
    if (u.id === profile?.id) { setError('Vous ne pouvez pas supprimer votre propre compte.'); return; }
    if (!window.confirm(`Supprimer définitivement ${u.full_name || u.email} ?`)) return;
    try {
      await usersApi.remove(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) { setError(formatApiError(e)); }
  };

  if (profile?.role !== 'superadmin') {
    return (
      <div className="h-screen flex flex-col">
        <Topbar />
        <div className="flex-1 flex items-center justify-center text-gray-500">Accès réservé aux super-administrateurs.</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#F4F6F9]" data-testid="admin-page">
      <Topbar />
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <h1 className="text-lg font-semibold text-gray-900">Administration plateforme</h1>
        <p className="text-xs text-gray-500 mt-0.5">Utilisateurs, rôles et mots de passe temporaires</p>
      </div>
      <div className="flex gap-2 px-6 pt-4 border-b border-gray-200 bg-white">
        {[
          { v: 'users', l: `👥 Utilisateurs (${users.length})` },
          { v: 'create', l: '➕ Créer un compte' },
        ].map((b) => (
          <button key={b.v} onClick={() => setTab(b.v)} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === b.v ? 'border-blue-600 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-testid={`admin-tab-${b.v}`}>
            {b.l}
          </button>
        ))}
      </div>

      <main className="flex-1 overflow-y-auto p-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 mb-4 text-sm">⚠️ {error}</div>}
        {msg && <div className="bg-green-50 border border-green-200 text-green-900 rounded-lg px-4 py-2.5 mb-4 text-sm">{msg}</div>}
        {lastCreated && (
          <div className="bg-yellow-50 border border-yellow-300 text-yellow-900 rounded-xl p-4 mb-4 text-sm" data-testid="last-created-banner">
            <div className="font-semibold mb-1">🔑 Identifiants à transmettre à l'utilisateur</div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><strong>Email&nbsp;:</strong> <span className="font-mono">{lastCreated.email}</span></div>
              <div><strong>Mot de passe temporaire&nbsp;:</strong> <span className="font-mono text-base bg-white px-2 py-0.5 rounded border border-yellow-400">{lastCreated.temp_password}</span></div>
            </div>
            <div className="text-xs mt-2 text-yellow-800">⚠️ L'utilisateur devra obligatoirement changer ce mot de passe à sa 1ʳᵉ connexion.</div>
            <button onClick={() => setLastCreated(null)} className="text-xs underline mt-2">Masquer</button>
          </div>
        )}

        {tab === 'users' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Rôle</th>
                  <th className="text-left px-4 py-3">Commune</th>
                  <th className="text-left px-4 py-3">Statut</th>
                  <th className="text-left px-4 py-3">PWD</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-500">Chargement…</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-500">Aucun utilisateur</td></tr>
                ) : (
                  users.map((u) => {
                    const cnom = communes.find((c) => c.id === u.commune_id)?.nom || '—';
                    return (
                      <tr key={u.id} className="border-t border-gray-100" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{u.full_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{u.email}</td>
                        <td className="px-4 py-3"><span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-900">{ROLES[u.role]?.label || u.role}</span></td>
                        <td className="px-4 py-3 text-gray-600">{cnom}</td>
                        <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${u.actif ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>{u.actif ? 'Actif' : 'Inactif'}</span></td>
                        <td className="px-4 py-3">{u.must_change_password && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-900">⚠️ Temp</span>}</td>
                        <td className="px-4 py-3 text-right space-x-1">
                          <button onClick={() => resetPassword(u)} className="text-xs px-2 py-1 rounded hover:bg-gray-100" data-testid={`user-reset-${u.id}`}>🔁 Reset mdp</button>
                          <button onClick={() => toggleActif(u)} className="text-xs px-2 py-1 rounded hover:bg-gray-100" data-testid={`user-toggle-${u.id}`}>{u.actif ? '🚫 Désactiver' : '✅ Activer'}</button>
                          <button onClick={() => removeUser(u)} className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50" data-testid={`user-delete-${u.id}`}>🗑️ Supprimer</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'create' && (
          <form onSubmit={createUser} className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl space-y-4" data-testid="create-user-form">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="create-email" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Nom complet *</label>
              <input required type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="create-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Rôle</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="create-role">
                  {Object.entries(ROLES).map(([v, r]) => <option key={v} value={v}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Commune</label>
                <select value={form.commune_id} onChange={(e) => setForm({ ...form, commune_id: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="create-commune">
                  <option value="">— Aucune —</option>
                  {communes.map((c) => <option key={c.id} value={c.id}>{c.nom} ({c.country_mode === 'senegal' ? '🌍' : '🍁'})</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Mot de passe temporaire (l'utilisateur devra le changer)</label>
              <div className="flex gap-2">
                <input type="text" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} className="flex-1 px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-mono outline-none focus:border-blue-600" data-testid="create-temp-pwd" />
                <button type="button" onClick={() => setForm({ ...form, temp_password: genTempPassword() })} className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50" data-testid="create-regen-pwd">🎲 Générer</button>
              </div>
            </div>
            <button type="submit" disabled={creating} className="px-5 py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-50" style={{ background: country.palette.primary }} data-testid="create-submit">
              {creating ? '…' : 'Créer le compte'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
